import _ from 'underscore';
import { QueryBuilder } from './query-builder';

import { ColumnChooserDecorator } from './decorators/column-chooser-decorator';
import { DateFilterDecorator } from './decorators/date-filter-decorator';
import { FilterDecorator } from './decorators/filter-decorator';
import { GridDataParametersDecorator } from './decorators/grid-data-parameters-decorator';
import { PaginationDecorator } from './decorators/pagination-decorator';
import { SegmentationDecorator } from './decorators/segmentation-decorator';
import { SortingDecorator } from './decorators/sorting-decorator';
import { ChartDataParametersDecorator } from './decorators/chart-parameters-decorator';
import { DataExclusionFilterProcessorDecorator } from './decorators/data-exclusion-filter-processor-decorator';
import { TargetRoasFilterProcessorDecorator } from './decorators/target-roas-filter-processor-decorator';

const decoratorMap = {
  columnChooser: ColumnChooserDecorator,
  dateFilter: DateFilterDecorator,
  segment: SegmentationDecorator,
  pagination: PaginationDecorator,
  filter: FilterDecorator,
  sorting: SortingDecorator,
  gridDataParameters: GridDataParametersDecorator,
  chartDataParameters: ChartDataParametersDecorator,
  dataExclusionFilterProcessor: DataExclusionFilterProcessorDecorator,
  targetRoasFilterProcessor: TargetRoasFilterProcessorDecorator,
};

function implementsDecoratorInterface(obj) {
  return obj && _.isFunction(obj.run);
}

function hasDecoratorConfiguration(obj) {
  return _.isObject(obj) && decoratorMap[obj.name];
}

function getDecoratorInstances(decorators = [], commonConfig = {}) {
  return decorators.map((decorator) => {
    let decoratorInstance = null;

    if (implementsDecoratorInterface(decorator)) {
      decoratorInstance = decorator;
    } else if (hasDecoratorConfiguration(decorator)) {
      const Decorator = decoratorMap[decorator.name];

      decoratorInstance = new Decorator(_.extend({}, commonConfig, decorator));
    } else {
      throw new Error(`Decorator ${decorator.name} was not found`);
    }

    return decoratorInstance;
  });
}

/**
 * Factory method that returns an instance of QueryBuilder
 * @param {Object} config - Configuration for the data query builder
 * @param {Object} [config.initialQuery] - Optional initial query, if not empty object
 * @param {Object} [config.logActivity] - Optional instrumentation activity with method
 *  error, trace, perf for logging QB metrics
 * @param {Object} config.commonConfig - Common configuration values. These will be passed in to
 * any decorators included in QB.
 * @param {Object} config.commonConfig.featureName - Feature name, used to
 * retrieve values from preference
 * @param {Array} decorators - Mixed array of decorator configurations for
 * decorators included in QB, and custom decorators instances.
 * Please note that decorators are run in the same order as in the array,
 * so be aware of dependencies.
 * For example, GridDataParametersDecorator should go last because it copies the other parameters
 * to odata action POST body.
 * Sample:
 * [
    {name: 'segment', // other options},
    {name: 'pagination' //other options},
    myCustomDecoratorInstance,
  ]
 * @returns {QueryBuilder} - The constructed data query builder
 */
export class QueryBuilderFactory {
  static make({
    initialQuery,
    logActivity,
    commonConfig,
    decorators,
  }) {
    const builderConfig = _.pick(commonConfig, 'generateJsDataQuery', 'featureName');

    const decoratorInstances = getDecoratorInstances(decorators, commonConfig);

    return new QueryBuilder({
      decorators: decoratorInstances,
      initialQuery,
      logActivity,
      builderConfig,
    });
  }
}
